import { Link } from "@tanstack/react-router";
import { ShoppingBag } from "lucide-react";
import { pickBookCover } from "#/lib/assets";
import {
  getLineItemImage,
  getLineItemTotal,
} from "#/lib/medusa-types/commerce";
import {
  useCart,
  useCartCount,
  useCartTotals,
  useFormatCartMoney,
  useRemoveCartLine,
  useUpdateCartLine,
} from "#/store/cart";
import { notify } from "../../lib/notify.js";
import { ROUTES } from "../../lib/routes.js";
import { Button } from "../ui/button.js";
import { QuantityStepper } from "../ui/quantity-stepper.js";
import { Separator } from "../ui/separator.js";
import {
  Sheet,
  SheetBody,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet.js";

export interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function CartDrawer({ open, onClose }: CartDrawerProps) {
  const { data: cart } = useCart();
  const count = useCartCount();
  const totals = useCartTotals();
  const formatCartMoney = useFormatCartMoney();
  const updateLine = useUpdateCartLine();
  const removeLine = useRemoveCartLine();

  const items = cart?.items ?? [];

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => !o && onClose()}
      side="right"
      size="lg"
    >
      <SheetHeader>
        <SheetTitle>Your Cart</SheetTitle>
        <SheetDescription>
          {count} {count === 1 ? "item" : "items"}
        </SheetDescription>
      </SheetHeader>
      <SheetBody className="p-0">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center mb-4">
              <ShoppingBag className="size-8 text-foreground" />
            </div>
            <div className="font-semibold text-lg text-foreground mb-1">
              Your cart is empty
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Add some books to get started.
            </p>
            <Link
              to={ROUTES.shop}
              onClick={onClose}
              className="inline-flex h-11 items-center rounded-full bg-primary px-6 font-semibold text-primary-foreground text-sm hover:bg-primary/90"
            >
              Browse the Shop
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((line) => {
              const thumb = getLineItemImage(line, pickBookCover);
              const subtitle =
                line.product_collection ??
                line.variant?.product?.collection?.title ??
                "";
              const lineTotal = getLineItemTotal(line);
              return (
                <div key={line.id} className="flex gap-4 p-5">
                  <img
                    src={thumb}
                    alt={line.title}
                    className="h-[100px] w-auto rounded-md object-contain shadow-md shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    {subtitle && (
                      <div className="text-xs text-muted-foreground">
                        {subtitle}
                      </div>
                    )}
                    <div className="font-semibold text-sm text-foreground line-clamp-2 mb-2">
                      {line?.variant_title ? line?.variant_title :line.title}
                    </div>
                    <div className="flex items-center gap-3">
                      <QuantityStepper
                        value={Number(line.quantity)}
                        onChange={(q) =>
                          updateLine.mutate({
                            lineItemId: line.id,
                            quantity: q,
                          })
                        }
                        size="sm"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          removeLine.mutate(
                            { lineItemId: line.id },
                            {
                              onSuccess: () => {
                                notify.success("Removed from cart");
                              },
                            },
                          );
                        }}
                        className="text-xs text-muted-foreground hover:text-destructive cursor-pointer bg-transparent border-0"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-foreground">
                    {formatCartMoney(lineTotal)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SheetBody>
      {items.length > 0 && (
        <SheetFooter className="space-y-3">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Subtotal</span>
            <span>{formatCartMoney(totals.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Shipping</span>
            <span>
              {totals.shippingTotal > 0
                ? formatCartMoney(totals.shippingTotal)
                : "Calculated at checkout"}
            </span>
          </div>
          <Separator />
          <div className="flex justify-between font-bold text-lg text-foreground">
            <span>Total</span>
            <span>{formatCartMoney(totals.total)}</span>
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <Link
              to={ROUTES.checkout}
              onClick={onClose}
              className="inline-flex h-12 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground text-base hover:bg-primary/90"
            >
              Checkout
            </Link>
            <Button variant="ghost" size="md" onClick={onClose}>
              Continue Shopping
            </Button>
          </div>
        </SheetFooter>
      )}
    </Sheet>
  );
}
